import React, { useEffect, useMemo, useState } from 'react';
import { LuArrowLeft, LuBookOpenText, LuLoaderPinwheel, LuPlus, LuSearch, LuSparkles, LuTrash2 } from 'react-icons/lu';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ProjectKnowledge, ProjectKnowledgeType } from '../../entities/project-knowledge';
import { deleteProjectKnowledge, knowledgeErrorMessage, listProjectKnowledge } from '../../shared/api/knowledge';
import { useProjectById } from '../../shared/contexts/Projects.provider';
import { auth } from '../../shared/firebase/firebase';
import useSocket from '../../shared/hooks/socket-hook';
import { TYPE_META } from '../../widgets/knowledge/type-meta';
import { ProviderBadge } from '../../widgets/ui/ProviderBadge';

// Full-page knowledge list for a project — reached from a "Knowledge" link on each provider's
// stats page. Deliberately its own page rather than embedded in the stats page body (MVPB-53
// feedback: it was crowding the stats page and forcing a click into Edit just to read a doc).
export default function KnowledgeListPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const project = useProjectById(projectId);
  const { on, off } = useSocket(auth.currentUser?.uid || '');

  const [docs, setDocs] = useState<ProjectKnowledge[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<ProjectKnowledgeType | 'all'>('all');
  const [search, setSearch] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const backTo = project?.provider === 'standalone' ? `/standalone/${projectId}` : `/dashboard/projects/${projectId}`;

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    listProjectKnowledge(projectId)
      .then((d) => !cancelled && setDocs(d))
      .catch((err) => !cancelled && setError(knowledgeErrorMessage(err) ?? 'Failed to load knowledge docs.'))
      .finally(() => !cancelled && setIsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // Live sync — knowledgeDocUpdated fires twice per doc (once on create/update, again once
  // rag-msa confirms it's embedded and sets indexedAt), so this also drives the "Indexed" badge
  // appearing without a manual refresh.
  useEffect(() => {
    if (!projectId) return;
    const onDocUpdated = (data: { userId: string; doc: ProjectKnowledge }) => {
      if (data.doc.projectId !== projectId) return;
      setDocs((prev) =>
        prev.some((d) => d._id === data.doc._id)
          ? prev.map((d) => (d._id === data.doc._id ? data.doc : d))
          : [...prev, data.doc],
      );
    };
    on('knowledgeDocUpdated', onDocUpdated);
    return () => off('knowledgeDocUpdated', onDocUpdated as (...args: unknown[]) => void);
  }, [projectId, on, off]);

  const counts = useMemo(() => {
    const c: Partial<Record<ProjectKnowledgeType, number>> = {};
    docs.forEach((d) => (c[d.type] = (c[d.type] ?? 0) + 1));
    return c;
  }, [docs]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return [...docs]
      .filter((d) => typeFilter === 'all' || d.type === typeFilter)
      .filter((d) => !q || d.title.toLowerCase().includes(q) || d.content.toLowerCase().includes(q))
      .sort((a, b) => b.createdAt - a.createdAt);
  }, [docs, typeFilter, search]);

  const handleDelete = async (doc: ProjectKnowledge, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!window.confirm(`Delete "${doc.title}"?`)) return;
    setDeletingId(doc._id);
    setError(null);
    try {
      await deleteProjectKnowledge(doc._id);
      setDocs((prev) => prev.filter((d) => d._id !== doc._id));
    } catch (err) {
      setError(knowledgeErrorMessage(err) ?? 'Failed to delete doc.');
    } finally {
      setDeletingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-base-200 flex items-center justify-center">
        <LuLoaderPinwheel className="animate-spin opacity-40" size={28} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base-200">
      <div className="p-4 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <Link to={backTo} className="btn btn-ghost btn-sm btn-circle shrink-0" title="Back to project">
              <LuArrowLeft size={16} />
            </Link>
            {project?.provider && <ProviderBadge provider={project.provider} />}
            <div className="min-w-0">
              <div className="text-lg font-semibold truncate">Project knowledge</div>
              <div className="text-xs opacity-50 truncate">{project?.name ?? 'Project'}</div>
            </div>
          </div>
          <button
            className="btn btn-primary btn-sm gap-1.5 shrink-0"
            onClick={() => navigate(`/dashboard/projects/${projectId}/knowledge/new`)}
          >
            <LuPlus size={14} />
            Add doc
          </button>
        </div>

        <p className="text-sm opacity-50 max-w-2xl">
          Docs your AI assistant reads for context on this project — metrics, workflow, conventions, guides.
        </p>

        {error && (
          <div className="alert alert-error py-2">
            <span className="text-sm">{error}</span>
          </div>
        )}

        {docs.length === 0 ? (
          <div className="card bg-base-100 shadow-card">
            <div className="card-body items-center text-center gap-2 py-12">
              <LuBookOpenText size={32} className="opacity-25" />
              <p className="text-sm opacity-60">No knowledge docs yet for this project.</p>
              <p className="text-xs opacity-40 max-w-sm">
                Add a metric definition, a workflow note, a convention, or a guide — the AI assistant will use it as
                context when answering questions about this project.
              </p>
              <button
                className="btn btn-primary btn-sm gap-1.5 mt-1"
                onClick={() => navigate(`/dashboard/projects/${projectId}/knowledge/new`)}
              >
                <LuPlus size={14} />
                Add your first doc
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                className={`badge cursor-pointer ${typeFilter === 'all' ? 'badge-neutral' : 'badge-outline opacity-60'}`}
                onClick={() => setTypeFilter('all')}
              >
                All ({docs.length})
              </button>
              {(Object.keys(TYPE_META) as ProjectKnowledgeType[]).map((t) => {
                const count = counts[t];
                if (!count) return null;
                const meta = TYPE_META[t];
                const active = typeFilter === t;
                return (
                  <button
                    key={t}
                    className={`badge gap-1 cursor-pointer ${active ? meta.badgeClass : 'badge-outline opacity-60'}`}
                    onClick={() => setTypeFilter(t)}
                  >
                    <meta.Icon size={11} />
                    {meta.label} ({count})
                  </button>
                );
              })}

              <label className="input input-bordered input-sm flex items-center gap-2 ml-auto max-w-[240px]">
                <LuSearch size={13} className="opacity-40" />
                <input
                  className="grow"
                  placeholder="Search docs…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </label>
            </div>

            {visible.length === 0 ? (
              <p className="text-sm opacity-40 py-8 text-center">No docs match this filter.</p>
            ) : (
              <div className="space-y-2">
                {visible.map((doc) => {
                  const meta = TYPE_META[doc.type];
                  return (
                    <Link
                      key={doc._id}
                      to={`/dashboard/projects/${projectId}/knowledge/${doc._id}`}
                      className="card bg-base-100 shadow-card hover:-translate-y-0.5 transition block"
                    >
                      <div className="card-body p-4 flex-row items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`badge badge-xs gap-1 ${meta.badgeClass}`}>
                              <meta.Icon size={10} />
                              {meta.label}
                            </span>
                            <span className="font-medium text-sm truncate">{doc.title}</span>
                            {doc.type === 'guide' && doc.indexedAt && (
                              <span
                                className="badge badge-xs badge-outline gap-1 opacity-60"
                                title="Indexed for AI semantic search"
                              >
                                <LuSparkles size={9} />
                                Indexed
                              </span>
                            )}
                          </div>
                          <p className="text-xs opacity-50 mt-1.5 line-clamp-2">{doc.content}</p>
                          {doc.tags && doc.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {doc.tags.map((t) => (
                                <span key={t} className="badge badge-ghost badge-xs">
                                  {t}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <button
                          className="btn btn-ghost btn-xs btn-circle shrink-0"
                          onClick={(e) => handleDelete(doc, e)}
                          disabled={deletingId === doc._id}
                          title="Delete"
                        >
                          {deletingId === doc._id ? (
                            <span className="loading loading-spinner loading-xs" />
                          ) : (
                            <LuTrash2 size={13} />
                          )}
                        </button>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
